import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { MuscleService } from '@entities/muscle'
import { useExerciseFilterStore } from '@entities/exercise'

const QUICK_MUSCLE_FILTERS = [
	{ label: 'Спина', groupCode: 'BACK' },
	{ label: 'Грудь', groupCode: 'CHEST' },
	{ label: 'Руки', groupCode: 'ARMS' },
	{ label: 'Ноги', groupCode: 'LEGS_GLUTES' },
	{ label: 'Плечи', groupCode: 'SHOULDERS' },
] as const

const areSameCodes = (left: string[], right: string[]): boolean => {
	if (left.length !== right.length) {
		return false
	}

	const rightSet = new Set(right)
	return left.every((code) => rightSet.has(code))
}

interface QuickExerciseFiltersProps {
	onOpenAdvancedFilters: () => void
}

export const QuickExerciseFilters: FC<QuickExerciseFiltersProps> = observer(
	({ onOpenAdvancedFilters }) => {
		const filterStore = useExerciseFilterStore()
		const { acceptedFilters } = filterStore
		const chipsCarouselRef = useRef<HTMLDivElement>(null)
		const [isFilterCompact, setIsFilterCompact] = useState<boolean>(false)
		const [hasFilterShadow, setHasFilterShadow] = useState<boolean>(false)
		const appliedFilterCount = filterStore.hasAcceptedAdvancedFilters
			? filterStore.acceptedSelectedCount
			: 0

		const syncFilterShape = useCallback(() => {
			const carousel = chipsCarouselRef.current

			if (!carousel) {
				return
			}

			const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth

			if (maxScrollLeft <= 2) {
				setIsFilterCompact(false)
				setHasFilterShadow(false)
				return
			}

			const isAtStart = carousel.scrollLeft <= 2

			setIsFilterCompact(!isAtStart)
			setHasFilterShadow(!isAtStart)
		}, [])

		useEffect(() => {
			syncFilterShape()
		}, [syncFilterShape])

		const quickMuscleCodes = useMemo(() => {
			return QUICK_MUSCLE_FILTERS.map((filter) => ({
				...filter,
				muscleCodes: MuscleService.getMuscleCodesFromGroupCode(
					filter.groupCode,
				),
			}))
		}, [])

		const activeQuickGroupCode = useMemo(() => {
			const activeGroup = quickMuscleCodes.find((filter) =>
				areSameCodes(acceptedFilters.includedMuscleCodes, filter.muscleCodes),
			)

			return activeGroup?.groupCode ?? null
		}, [acceptedFilters.includedMuscleCodes, quickMuscleCodes])

		const hasAdvancedFilters = filterStore.hasAcceptedAdvancedFilters

		const isAllActive =
			acceptedFilters.includedMuscleCodes.length === 0 && !hasAdvancedFilters

		return (
			<div
				className={`training-filters-shell ${isFilterCompact ? 'is-compact' : ''}`}
			>
				<button
					type="button"
					className={`training-filter-chip training-filter-toggle ${hasAdvancedFilters ? 'is-active' : ''} ${isFilterCompact ? 'is-compact' : ''} ${hasFilterShadow ? 'has-carousel-shadow' : ''}`}
					aria-pressed={hasAdvancedFilters}
					aria-label={
						appliedFilterCount > 0
							? `Открыть расширенные фильтры, выбрано ${appliedFilterCount}`
							: 'Открыть расширенные фильтры'
					}
					onClick={onOpenAdvancedFilters}
				>
					<SlidersHorizontal aria-hidden="true" />
					<span className="training-filter-chip-text">
						{appliedFilterCount > 0 ? `Фильтр · ${appliedFilterCount}` : 'Фильтр'}
					</span>
					{appliedFilterCount > 0 && (
						<span className="training-filter-badge">{appliedFilterCount}</span>
					)}
				</button>
				<div
					ref={chipsCarouselRef}
					className="training-chips-carousel"
					aria-label="Быстрые фильтры упражнений"
					onScroll={syncFilterShape}
				>
					<button
						type="button"
						className={`training-filter-chip ${isAllActive ? 'is-active' : ''}`}
						aria-pressed={isAllActive}
						onClick={() => filterStore.applyQuickMuscleFilter([])}
					>
						Все
					</button>
					{quickMuscleCodes.map((filter) => {
						const isActive =
							activeQuickGroupCode === filter.groupCode && !hasAdvancedFilters

						return (
							<button
								key={filter.groupCode}
								type="button"
								className={`training-filter-chip ${isActive ? 'is-active' : ''}`}
								aria-pressed={isActive}
								onClick={() =>
									filterStore.applyQuickMuscleFilter(filter.muscleCodes)
								}
							>
								{filter.label}
							</button>
						)
					})}
				</div>
			</div>
		)
	},
)
